import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, ChevronRight, X } from "lucide-react";
import { axiosInstance } from "@/lib/api";
import { queue } from "@/components/Toast";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { cn } from "@/utils/helpers";
import {
  MAX_POSTS_PER_MONTH,
  MIN_POSTS_PER_MONTH,
  type BrandCalendarSettings,
  type CalendarClientSendPolicy,
  type CalendarSettingsInput,
} from "@/types/contentCalendar.type";
import { kitButtonClass } from "../../components/kit/Button";
import { KitCheckbox, KitRadio, KitRadioGroup } from "../../components/kit/Checkbox";
import { KitIconButton } from "../../components/kit/IconButton";
import { KitSelect, KitSelectItem } from "../../components/kit/Select";
import { KitStepper } from "../../components/kit/Switch";
import { useUpdateCalendarSettings } from "../api/useContentCalendar";
import { MONTH_NAMES } from "../calendar.utils";
import { CalendarSheet, type CalendarViewport } from "../month/CalendarSheet";

/**
 * The brand's calendar settings (HfE0k desktop, oMYXC tablet, rjwTg phone):
 * the page title and its line, then Calendar / Settings panel, one
 * Calendar / Settings section per subject (Posts a month, Planning, Time zone,
 * Posting times), each a 200px label block beside its controls, then the
 * footer line and Save changes. A phone stacks the Planning card · Phone and
 * the Settings card · Phone with Save at the foot.
 *
 * The scope is three numbers rather than one total because each format is a
 * different production job; the total is their sum, so the two can never
 * disagree. The limits are the server's: 0 to 30 per format, 1 to 30 in all.
 *
 * Planning on a schedule is the nightly planner (autoPlanEnabled): on the
 * chosen day of each month (planDayOfMonth) it plans the month at the far edge
 * of the planning period (planningPeriodMonths: 1 is "the month after"). The
 * Every month / 2 weeks / week cadence of the Pen (nyDlR, LyMfV) waits on the
 * backend, so its row is not drawn.
 */

const FORMAT_FIELDS = [
  { key: "staticPostsPerMonth", label: "Static" },
  { key: "verticalVideoPostsPerMonth", label: "Vertical video" },
  { key: "horizontalVideoPostsPerMonth", label: "Horizontal video" },
] as const;

type FormatKey = (typeof FORMAT_FIELDS)[number]["key"];

/** Curated IANA zones covering the brand base; the device's own zone and the saved value are always added. */
const COMMON_TIMEZONES = [
  "UTC",
  "Asia/Dubai",
  "Asia/Riyadh",
  "Asia/Qatar",
  "Asia/Kuwait",
  "Asia/Bahrain",
  "Asia/Muscat",
  "Africa/Cairo",
  "Asia/Amman",
  "Asia/Beirut",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Istanbul",
  "Europe/Moscow",
  "Africa/Lagos",
  "Africa/Johannesburg",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Dhaka",
  "Asia/Jakarta",
  "Asia/Singapore",
  "Asia/Hong_Kong",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
  "America/Sao_Paulo",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Mexico_City",
] as const;

/** "Dubai, GMT+4": the city, then the zone's offset today. */
function zoneLabel(zone: string): string {
  if (zone === "UTC") return "UTC";
  const city = (zone.split("/").pop() ?? zone).replace(/_/g, " ");
  try {
    const offset = new Intl.DateTimeFormat("en-GB", { timeZone: zone, timeZoneName: "shortOffset" })
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value;
    return offset ? `${city}, ${offset}` : city;
  } catch {
    return city;
  }
}

function timezoneOptions(saved: string): { id: string; label: string }[] {
  const set = new Set<string>(COMMON_TIMEZONES);
  const device = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (device) set.add(device);
  if (saved) set.add(saved);
  return [...set].map((id) => ({ id, label: zoneLabel(id) }));
}

/** Every half hour, plus the saved time when it falls between. */
function timeOptions(saved: string): { id: string; label: string }[] {
  const set = new Set<string>();
  for (let h = 0; h < 24; h++) for (const m of ["00", "30"]) set.add(`${String(h).padStart(2, "0")}:${m}`);
  if (saved) set.add(saved);
  return [...set].sort().map((id) => ({ id, label: id }));
}

function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  return `${n}${({ 1: "st", 2: "nd", 3: "rd" } as Record<number, string>)[n % 10] ?? "th"}`;
}

const DAY_OPTIONS = Array.from({ length: 28 }, (_, i) => ({ id: String(i + 1), label: ordinal(i + 1) }));

/** How far ahead a run plans, said from the run's own month. */
const HORIZON_OPTIONS = [
  { id: "1", label: "the month after" },
  { id: "2", label: "the month after next" },
  { id: "3", label: "three months ahead" },
];

/**
 * Reply by (Figma 08 Settings → Calendar): the client's default reply-by day on
 * every send, as days before the first post. The send dialog changes it for one
 * send. "none" asks for no date.
 */
const REPLY_BY_OPTIONS = [
  ...[3, 5, 7, 9, 14].map((days) => ({ id: String(days), label: `${days} days before the post` })),
  { id: "none", label: "Don’t ask for a date" },
];

/** "Mon 5 Oct" from "2026-10-05". */
function shortDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}

/** What GET /settings carries beside the settings (backend #122, #123). */
interface SettingsMeta {
  settings: BrandCalendarSettings;
  strategyScope: {
    strategyId: string;
    strategyTitle: string;
    staticPostsPerMonth: number;
    verticalVideoPostsPerMonth: number;
    horizontalVideoPostsPerMonth: number;
    total: number;
  } | null;
  nextRun: { date: string; year: number; month: number; expectedPosts: number; withIdeas: boolean } | null;
}

const settingsKey = (brandId: string) => ["calendarSettings", brandId];
const metaKey = (brandId: string) => ["calendarSettingsMeta", brandId];

/** The settings with the strategy's numbers and the next run (the year page's Posts a month reads it too). */
export function useSettingsMeta(brandId: string, enabled = true) {
  return useQuery({
    enabled: enabled && !!brandId,
    queryKey: metaKey(brandId),
    queryFn: async () => (await axiosInstance.get<SettingsMeta>(`/content-calendar/${brandId}/settings`)).data,
  });
}

/** Use the strategy's numbers: the brand's scope follows the active strategy again. */
export function useTakeStrategyScope(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await axiosInstance.post<{ settings: BrandCalendarSettings }>(`/content-calendar/${brandId}/settings/use-strategy-scope`))
        .data.settings,
    onSuccess: (next) => {
      qc.setQueryData(settingsKey(brandId), next);
      void qc.invalidateQueries({ queryKey: metaKey(brandId) });
    },
    onError: () => queue.add({ title: "Could not use the strategy’s numbers", description: "Please try again.", variant: "error" }),
  });
}

/** A Select in control glass at 36, as the panel draws it. */
function PanelSelect({
  label,
  value,
  options,
  onChange,
  width,
  disabled,
}: {
  label: string;
  value: string;
  options: { id: string; label: string }[];
  onChange: (next: string) => void;
  width: string;
  disabled?: boolean;
}) {
  return (
    <KitSelect
      aria-label={label}
      selectedKey={value}
      items={options}
      isDisabled={disabled}
      onSelectionChange={(key) => key != null && onChange(String(key))}
      className={width}
      buttonClassName="h-9 min-w-0 px-3.5"
    >
      {(item: { id: string; label: string }) => (
        <KitSelectItem id={item.id} textValue={item.label}>
          {item.label}
        </KitSelectItem>
      )}
    </KitSelect>
  );
}

/** Calendar / Settings section: the label block (200) beside the section's controls. */
function SettingsSection({
  id,
  title,
  description,
  last = false,
  children,
}: {
  id: string;
  title: string;
  description?: ReactNode;
  last?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      aria-labelledby={`${id}-title`}
      className={cn("flex flex-col gap-3 px-5 py-5 sm:flex-row sm:gap-6 sm:px-6", !last && "shadow-[inset_0_-1px_0_var(--co-line)]")}
    >
      <div className="flex shrink-0 flex-col gap-1 sm:w-[200px]">
        <h3 id={`${id}-title`} className="font-body text-[14px] font-semibold leading-normal text-co-text">
          {title}
        </h3>
        {description ? <p className="text-[12px] leading-[1.45] text-co-muted">{description}</p> : null}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-3">{children}</div>
    </section>
  );
}

/** Calendar / Settings note: the next run, on the recess. */
function SettingsNote({ children, phone }: { children: ReactNode; phone?: boolean }) {
  return (
    <p className={cn("flex gap-2 rounded-card bg-co-recess px-3 py-2.5 text-[13px] leading-[1.4] text-co-text", phone ? "items-start" : "items-center")}>
      <CalendarClock className={cn("size-[15px] shrink-0 text-co-accent-ink", phone && "mt-px")} aria-hidden="true" />
      <span className="min-w-0">{children}</span>
    </p>
  );
}

/** A phone row whose value opens the platform's own picker (a native select laid over it). */
function PhonePickRow({
  label,
  value,
  options,
  display,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  options: { id: string; label: string }[];
  display: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  return (
    <label className="relative flex h-[50px] items-center gap-2 pl-3.5 pr-3 shadow-[inset_0_1px_0_var(--co-line)]">
      <span className="text-[15px] text-co-text">{label}</span>
      <span className="min-w-0 flex-1 truncate text-right text-[15px] text-co-muted">{display}</span>
      <ChevronRight className="size-4 shrink-0 text-co-muted" aria-hidden="true" />
      <select
        aria-label={label}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 cursor-pointer opacity-0"
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function CalendarSettingsModal({
  brandId,
  settings,
  onClose,
  brandName = null,
  viewport = "desktop",
  inline = false,
}: {
  brandId: string;
  settings: BrandCalendarSettings;
  onClose: () => void;
  brandName?: string | null;
  viewport?: CalendarViewport;
  /** Settings → Calendar (Figma 09 · Form pages / 12): the panel placed on the page, no sheet or close. */
  inline?: boolean;
}) {
  const phone = viewport === "phone" && !inline;
  const qc = useQueryClient();
  const { selectedOrganization } = useCurrentUser();
  const isOwner = selectedOrganization?.role === "Owner";
  const meta = useSettingsMeta(brandId);
  const update = useUpdateCalendarSettings(brandId);

  const [mix, setMix] = useState<Record<FormatKey, number>>({
    staticPostsPerMonth: settings.staticPostsPerMonth,
    verticalVideoPostsPerMonth: settings.verticalVideoPostsPerMonth,
    horizontalVideoPostsPerMonth: settings.horizontalVideoPostsPerMonth,
  });
  const [period, setPeriod] = useState(settings.planningPeriodMonths);
  const [autoPlan, setAutoPlan] = useState(settings.autoPlanEnabled);
  const [planDay, setPlanDay] = useState<number | null>(settings.planDayOfMonth ?? null);
  const [withIdeas, setWithIdeas] = useState(settings.writeIdeasWithPlan ?? false);
  const [timezone, setTimezone] = useState(settings.timezone || "UTC");
  const [publishTime, setPublishTime] = useState(settings.defaultPublishTime || "12:00");
  const savedReplyBy = settings.replyByDaysBefore === undefined ? 7 : settings.replyByDaysBefore;
  const [replyBy, setReplyBy] = useState<number | null>(savedReplyBy);
  const replyByValue = replyBy === null ? "none" : String(replyBy);
  const replyByOptions = REPLY_BY_OPTIONS.some((option) => option.id === replyByValue)
    ? REPLY_BY_OPTIONS
    : [{ id: replyByValue, label: `${replyBy} days before the post` }, ...REPLY_BY_OPTIONS];
  const onReplyBy = (next: string) => setReplyBy(next === "none" ? null : Number(next));
  // Who can send to the client (Figma 08, Calendar / Sending panel): an Owner's call.
  const savedSendPolicy: CalendarClientSendPolicy = settings.clientSendPolicy ?? "ANYONE";
  const [sendPolicy, setSendPolicy] = useState<CalendarClientSendPolicy>(savedSendPolicy);

  const takeStrategy = useTakeStrategyScope(brandId);

  const tzOptions = useMemo(() => timezoneOptions(timezone), [timezone]);
  const times = useMemo(() => timeOptions(publishTime), [publishTime]);
  const total = mix.staticPostsPerMonth + mix.verticalVideoPostsPerMonth + mix.horizontalVideoPostsPerMonth;
  const totalValid = total >= MIN_POSTS_PER_MONTH && total <= MAX_POSTS_PER_MONTH;
  const totalMessage =
    total < MIN_POSTS_PER_MONTH
      ? "A month needs at least one post. Give at least one format a count."
      : total > MAX_POSTS_PER_MONTH
        ? `That is ${total} posts a month; the most across all formats is ${MAX_POSTS_PER_MONTH}.`
        : null;
  const possessive = brandName?.trim() ? `${brandName.trim()}’s` : "this brand’s";
  const busy = update.isPending || takeStrategy.isPending;

  const planningDirty =
    period !== settings.planningPeriodMonths ||
    autoPlan !== settings.autoPlanEnabled ||
    planDay !== (settings.planDayOfMonth ?? null) ||
    withIdeas !== (settings.writeIdeasWithPlan ?? false) ||
    total !== settings.staticPostsPerMonth + settings.verticalVideoPostsPerMonth + settings.horizontalVideoPostsPerMonth;
  const nextRun = meta.data?.nextRun ?? null;
  const strategyScope = meta.data?.strategyScope ?? null;
  const source = meta.data?.settings?.scopeSource ?? settings.scopeSource ?? null;
  const strategyDiffers =
    !!strategyScope &&
    (strategyScope.staticPostsPerMonth !== mix.staticPostsPerMonth ||
      strategyScope.verticalVideoPostsPerMonth !== mix.verticalVideoPostsPerMonth ||
      strategyScope.horizontalVideoPostsPerMonth !== mix.horizontalVideoPostsPerMonth);

  const sourceLine =
    source === "STRATEGY"
      ? `Set from ${possessive} strategy.`
      : source === "PERSON"
        ? "Set by your team."
        : "The starting numbers. Nobody has set them yet.";

  const runLine = !autoPlan
    ? "Off: each month is planned by hand, from the calendar."
    : planningDirty
      ? "Save to see when the next run plans."
      : nextRun
        ? `Next run ${shortDate(nextRun.date)} plans ${MONTH_NAMES[nextRun.month - 1]}, ${nextRun.expectedPosts} ${nextRun.expectedPosts === 1 ? "post" : "posts"}${nextRun.withIdeas ? ", with ideas" : ""}.`
        : null;
  const runFollow =
    autoPlan && !planningDirty && nextRun
      ? `The team opens the calendar on ${Number(nextRun.date.slice(8))} ${MONTH_NAMES[Number(nextRun.date.slice(5, 7)) - 1].slice(0, 3)} to find ${MONTH_NAMES[nextRun.month - 1]} planned, reviews it, then ${nextRun.withIdeas ? "picks ideas" : "asks for ideas post by post"}.`
      : null;

  const save = () => {
    if (!totalValid || busy) return;
    const payload: CalendarSettingsInput = {
      planningPeriodMonths: period,
      staticPostsPerMonth: mix.staticPostsPerMonth,
      verticalVideoPostsPerMonth: mix.verticalVideoPostsPerMonth,
      horizontalVideoPostsPerMonth: mix.horizontalVideoPostsPerMonth,
      autoPlanEnabled: autoPlan,
      timezone,
      defaultPublishTime: publishTime,
    };
    if (planDay !== (settings.planDayOfMonth ?? null)) payload.planDayOfMonth = planDay;
    if (replyBy !== savedReplyBy) payload.replyByDaysBefore = replyBy;
    // Owner-only on the server: sent only when an Owner changed it.
    if (isOwner && withIdeas !== (settings.writeIdeasWithPlan ?? false)) payload.writeIdeasWithPlan = withIdeas;
    if (isOwner && sendPolicy !== savedSendPolicy) payload.clientSendPolicy = sendPolicy;
    // Optimistic: the brand's settings take the new values at once and close;
    // a refusal puts the old ones back, and the hook says why.
    const key = settingsKey(brandId);
    const previous = qc.getQueryData<BrandCalendarSettings>(key);
    qc.setQueryData<BrandCalendarSettings>(key, { ...settings, ...previous, ...payload } as BrandCalendarSettings);
    update.mutate(payload, {
      onError: () => {
        if (previous) qc.setQueryData(key, previous);
      },
      onSettled: () => void qc.invalidateQueries({ queryKey: metaKey(brandId) }),
    });
    onClose();
  };

  const ideasLabel = phone ? "Write 3 ideas per post" : "Write 3 ideas per post when planned";
  const ideasBox = (
    <KitCheckbox
      isSelected={withIdeas}
      onChange={setWithIdeas}
      isDisabled={busy || !isOwner}
      className={phone ? "text-[15px] text-co-text" : "text-[14px] text-co-text"}
    >
      {ideasLabel}
    </KitCheckbox>
  );
  const scheduleBox = (
    <KitCheckbox
      isSelected={autoPlan}
      onChange={setAutoPlan}
      isDisabled={busy}
      className={phone ? "text-[15px] text-co-text" : "text-[14px] text-co-text"}
    >
      Plan on a schedule
    </KitCheckbox>
  );
  const dayValue = String(planDay ?? 1);
  const onDay = (next: string) => setPlanDay(Number(next));
  const onHorizon = (next: string) => setPeriod(Number(next));
  const scopeNote = (
    <p className="text-[12px] leading-[1.45] text-co-muted">
      Your numbers stay when the strategy changes.
    </p>
  );
  const useStrategyButton =
    source === "PERSON" && strategyDiffers && strategyScope ? (
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          setMix({
            staticPostsPerMonth: strategyScope.staticPostsPerMonth,
            verticalVideoPostsPerMonth: strategyScope.verticalVideoPostsPerMonth,
            horizontalVideoPostsPerMonth: strategyScope.horizontalVideoPostsPerMonth,
          });
          takeStrategy.mutate();
        }}
        className={cn(kitButtonClass({ variant: "ghost" }), "-ml-3.5 self-start")}
      >
        Use the strategy’s numbers, {strategyScope.total} posts
      </button>
    ) : null;
  const alert = totalMessage ? (
    <p role="alert" className="text-[13px] text-co-alert-ink">
      {totalMessage}
    </p>
  ) : null;
  const clientWord = brandName?.trim() || "the client";
  const approvalsLine = `Any of ${clientWord}’s people can approve.`;
  // Who can send to the client: two choices, each with its one-line consequence.
  const sendChoices: Array<{ value: CalendarClientSendPolicy; label: string; line: string }> = [
    { value: "ANYONE", label: "Anyone on the team", line: `Anyone on the team can send ideas and briefs to ${clientWord}.` },
    { value: "OWNERS", label: "Owners only", line: "Only owners can send; others ask them." },
  ];
  const sendPolicyGroup = (
    <KitRadioGroup
      aria-label="Who can send to the client"
      value={sendPolicy}
      onChange={(next) => setSendPolicy(next as CalendarClientSendPolicy)}
      isDisabled={busy || !isOwner}
      className={cn("gap-1", phone && "px-1 py-1")}
    >
      {sendChoices.map((choice) => (
        <KitRadio
          key={choice.value}
          value={choice.value}
          className={cn(
            "items-start rounded-card px-2.5 py-2.5 transition-colors",
            sendPolicy === choice.value ? "bg-co-tonal" : "hover:bg-co-hover",
          )}
        >
          <span className="flex flex-col gap-0.5">
            <span className={cn("font-medium text-co-text", phone ? "text-[15px]" : "text-[14px]")}>{choice.label}</span>
            <span className="text-[12px] leading-[1.45] text-co-muted">{choice.line}</span>
          </span>
        </KitRadio>
      ))}
    </KitRadioGroup>
  );
  const sendPolicyNote = isOwner ? null : (
    <p className="max-w-[460px] text-[12px] leading-[1.45] text-co-muted">An Owner changes who can send.</p>
  );

  if (phone) {
    return (
      <CalendarSheet phone large label="Calendar settings" onClose={onClose} dismissable={!busy} flush grabber={false}>
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto px-4 pb-4 pt-4">
            <div className="flex items-start gap-2">
              <div className="flex min-w-0 flex-1 flex-col gap-1.5 pt-2">
                <h2 className="font-display text-[34px] font-bold leading-[1.1] text-co-text">Calendar</h2>
                <p className="text-[14px] text-co-text2">How {possessive} calendar plans.</p>
              </div>
              <KitIconButton kind="close" size="xl" icon={X} iconClassName="size-3.5" aria-label="Close" onClick={onClose} />
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-[13px] font-semibold text-co-text2">Planning</p>
              <div className="flex flex-col overflow-hidden rounded-card bg-co-surface">
                <div className="flex h-[50px] items-center px-3.5">{scheduleBox}</div>
                {autoPlan ? (
                  <>
                    <PhonePickRow
                      label="On the"
                      value={dayValue}
                      options={DAY_OPTIONS}
                      display={`${ordinal(planDay ?? 1)} of each month`}
                      onChange={onDay}
                      disabled={busy}
                    />
                    <PhonePickRow
                      label="For"
                      value={String(period)}
                      options={HORIZON_OPTIONS}
                      display={(HORIZON_OPTIONS.find((o) => o.id === String(period))?.label ?? "").replace(/^t/, "T")}
                      onChange={onHorizon}
                      disabled={busy}
                    />
                    <div className="flex h-[50px] items-center px-3.5 shadow-[inset_0_1px_0_var(--co-line)]">{ideasBox}</div>
                  </>
                ) : null}
              </div>
            </div>
            {runLine ? <SettingsNote phone>{runLine}</SettingsNote> : null}

            <div className="flex flex-col gap-2">
              <p className="text-[13px] font-semibold text-co-text2">Posts a month</p>
              <div className="flex flex-col rounded-card border border-co-line2 bg-co-surface">
                {FORMAT_FIELDS.map(({ key, label }, i) => (
                  <div
                    key={key}
                    className={cn(
                      "flex h-[52px] items-center gap-2 pl-3.5 pr-3",
                      i < FORMAT_FIELDS.length - 1 && "shadow-[inset_0_-1px_0_var(--co-line)]",
                    )}
                  >
                    <span className="min-w-0 flex-1 text-[15px] text-co-text">{label}</span>
                    <KitStepper
                      label={label}
                      value={mix[key]}
                      min={0}
                      max={MAX_POSTS_PER_MONTH}
                      disabled={busy}
                      onChange={(n) => setMix((current) => ({ ...current, [key]: n }))}
                    />
                  </div>
                ))}
              </div>
              <p className="text-[12px] leading-[1.45] text-co-muted">{sourceLine}</p>
              {useStrategyButton}
              {alert}
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-[13px] font-semibold text-co-text2">Time zone and posting times</p>
              <div className="flex flex-col rounded-card border border-co-line2 bg-co-surface [&>label:first-child]:shadow-none">
                <PhonePickRow
                  label="Time zone"
                  value={timezone}
                  options={tzOptions}
                  display={zoneLabel(timezone)}
                  onChange={setTimezone}
                  disabled={busy}
                />
                <PhonePickRow
                  label="When there is nothing to go on"
                  value={publishTime}
                  options={times}
                  display={publishTime}
                  onChange={setPublishTime}
                  disabled={busy}
                />
              </div>
              <p className="text-[12px] leading-[1.45] text-co-muted">
                Each channel gets a suggested time. Move any.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-[13px] font-semibold text-co-text2">Reply by</p>
              <div className="flex flex-col rounded-card border border-co-line2 bg-co-surface [&>label:first-child]:shadow-none">
                <PhonePickRow
                  label="Reply by"
                  value={replyByValue}
                  options={replyByOptions}
                  display={replyByOptions.find((option) => option.id === replyByValue)?.label ?? ""}
                  onChange={onReplyBy}
                  disabled={busy}
                />
              </div>
              <p className="text-[12px] leading-[1.45] text-co-muted">
                {possessive.charAt(0).toUpperCase() + possessive.slice(1)} default on every send. Change it per post in the send dialog.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-[13px] font-semibold text-co-text2">Approvals</p>
              <p className="text-[14px] leading-[1.5] text-co-text">{approvalsLine}</p>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-[13px] font-semibold text-co-text2">Who can send to the client</p>
              <div className="flex flex-col rounded-card border border-co-line2 bg-co-surface">{sendPolicyGroup}</div>
              {sendPolicyNote}
            </div>
          </div>
          <div className="px-2 pb-2 pt-2">
            <button
              type="button"
              onClick={save}
              disabled={!totalValid || busy}
              className={cn(kitButtonClass({ variant: "primary" }), "h-12 w-full text-[15px] font-semibold")}
            >
              Save changes
            </button>
          </div>
        </div>
      </CalendarSheet>
    );
  }

  const body = (
      <div className="flex flex-col gap-4">
        {inline ? null : (
        <header className="flex items-start gap-3 pb-4 shadow-[inset_0_-1px_0_var(--co-line)]">
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <h2 className="font-section text-[26px] font-bold leading-[1.15] text-co-text">Calendar</h2>
            <p className="max-w-[560px] text-[13px] leading-[1.5] text-co-muted">
              How {possessive} calendar plans: how many posts a month, when new posts get planned, the time zone and
              posting times.
            </p>
          </div>
          <KitIconButton kind="close" icon={X} aria-label="Close" disabled={busy} onClick={onClose} />
        </header>
        )}

        <div className={cn("flex flex-col bg-co-surface", inline ? "rounded-panel" : "border border-co-line2")}>
          <SettingsSection id="cal-settings-scope" title="Posts a month" description={sourceLine}>
            {FORMAT_FIELDS.map(({ key, label }) => (
              <div key={key} className="flex w-full max-w-[360px] items-center gap-2">
                <span className="min-w-0 flex-1 text-[14px] text-co-text2">{label}</span>
                <KitStepper
                  label={label}
                  value={mix[key]}
                  min={0}
                  max={MAX_POSTS_PER_MONTH}
                  disabled={busy}
                  onChange={(n) => setMix((current) => ({ ...current, [key]: n }))}
                />
              </div>
            ))}
            <div className="flex w-full max-w-[360px] items-center gap-2 pt-2 shadow-[inset_0_1px_0_var(--co-line)]">
              <span className="flex-1 text-[14px] font-semibold text-co-text">Every month</span>
              <span className={cn("text-[14px] font-semibold tabular-nums", totalValid ? "text-co-text" : "text-co-alert-ink")}>
                {total} {total === 1 ? "post" : "posts"}
              </span>
            </div>
            {alert}
            <div className="max-w-[420px]">{scopeNote}</div>
            {useStrategyButton}
          </SettingsSection>

          <SettingsSection
            id="cal-settings-planning"
            title="Planning"
            description="How often, and how far ahead."
          >
            {scheduleBox}
            {autoPlan ? (
              <>
                <div className="flex flex-wrap items-center gap-2.5 text-[14px] text-co-text2">
                  <span>On the</span>
                  <PanelSelect label="Day of the month" value={dayValue} options={DAY_OPTIONS} onChange={onDay} width="w-[90px]" disabled={busy} />
                  <span>of each month, for</span>
                  <PanelSelect
                    label="How far ahead"
                    value={String(period)}
                    options={HORIZON_OPTIONS}
                    onChange={onHorizon}
                    width="w-[180px]"
                    disabled={busy}
                  />
                </div>
                {ideasBox}
              </>
            ) : null}
            {runLine ? <SettingsNote>{runLine}</SettingsNote> : null}
            {runFollow ? <p className="max-w-[460px] text-[12px] leading-[1.45] text-co-muted">{runFollow}</p> : null}
            {autoPlan && !isOwner ? (
              <p className="max-w-[460px] text-[12px] leading-[1.45] text-co-muted">
                Writing ideas with each run spends the workspace’s budget, so an Owner turns it on or off.
              </p>
            ) : null}
          </SettingsSection>

          <SettingsSection id="cal-settings-zone" title="Time zone" description="Posting times use this zone.">
            <PanelSelect label="Time zone" value={timezone} options={tzOptions} onChange={setTimezone} width="w-[260px]" disabled={busy} />
          </SettingsSection>

          <SettingsSection id="cal-settings-times" title="Posting times" last>
            <p className="max-w-[460px] text-[14px] leading-[1.5] text-co-text2">
              Each channel gets a suggested time. Move any.
            </p>
            <div className="flex items-center gap-3">
              <span className="text-[13px] text-co-muted">When there is nothing to go on</span>
              <PanelSelect
                label="When there is nothing to go on"
                value={publishTime}
                options={times}
                onChange={setPublishTime}
                width="w-[110px]"
                disabled={busy}
              />
            </div>
          </SettingsSection>
        </div>

        <div className={cn("flex flex-col bg-co-surface", inline ? "rounded-panel" : "border border-co-line2")}>
          <SettingsSection
            id="cal-settings-reply"
            title="Reply by"
            description={`${possessive} default on every send. Change it per post in the send dialog.`}
          >
            <PanelSelect label="Reply by" value={replyByValue} options={replyByOptions} onChange={onReplyBy} width="w-[260px]" disabled={busy} />
          </SettingsSection>

          <SettingsSection id="cal-settings-approvals" title="Approvals">
            <p className="text-[14px] leading-[21px] text-co-text2">{approvalsLine}</p>
          </SettingsSection>

          <SettingsSection id="cal-settings-send" title="Who can send to the client" last>
            <div className="w-full max-w-[446px]">{sendPolicyGroup}</div>
            {sendPolicyNote}
          </SettingsSection>
        </div>

        <div className="flex items-center gap-3">
          <p className="min-w-0 flex-1 text-[13px] text-co-muted">Planned posts keep their numbers. Change a month from the month.</p>
          <button type="button" onClick={save} disabled={!totalValid || busy} className={kitButtonClass({ variant: "primary" })}>
            Save changes
          </button>
        </div>
      </div>
  );

  if (inline) return body;

  return (
    <CalendarSheet
      phone={false}
      label="Calendar settings"
      onClose={onClose}
      dismissable={!busy}
      width={viewport === "tablet" ? "max-w-[800px]" : "max-w-[876px]"}
    >
      {body}
    </CalendarSheet>
  );
}
